import { ComponentType, useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { DsmLoadingIndicator } from "@dsm-dcs/design-system-react";
import { Farm } from "../../../../graphql/types";
import { fetchProductionProcessBaselines } from "../../apicalls/farmFunctions";

export type DatasetRouteState = {
  farmName?: string;
  processName?: string;
  animalType?: string;
} & Record<string, unknown>;

export type DatasetRouteProps = {
  match: {
    params: { customerID: string; farmID: string; processID: string };
  };
  location: { state?: DatasetRouteState } & Record<string, unknown>;
};

type ResolvedProcessData = Pick<
  DatasetRouteState,
  "farmName" | "processName" | "animalType"
>;

type ResolvedProcess = { key: string; data: ResolvedProcessData };

const withResolvedRouteState = <P extends DatasetRouteProps>(
  WrappedPage: ComponentType<P>
) => {
  const PageWithResolvedRouteState = (props: P) => {
    const { match, location } = props;
    const { customerID, farmID, processID } = match.params;
    const routeState = location.state;
    const history = useHistory();
    const [resolvedProcess, setResolvedProcess] = useState<ResolvedProcess>();

    const needsResolving = !routeState?.animalType;
    const routeKey = `${farmID}/${processID}`;
    const processData =
      resolvedProcess?.key === routeKey ? resolvedProcess.data : undefined;

    const fallBackToProcessPage = useCallback(
      () =>
        history.replace(
          `/customers/${customerID}/farm/${farmID}/pp/${processID}`
        ),
      [history, customerID, farmID, processID]
    );

    useEffect(() => {
      if (!needsResolving || processData) return undefined;

      let active = true;

      fetchProductionProcessBaselines(
        farmID,
        processID,
        (farm: Farm) => {
          if (!active) return;
          const animalSystem = farm?.animalSystems?.items?.[0];
          if (!animalSystem?.animalType) {
            fallBackToProcessPage();
            return;
          }
          setResolvedProcess({
            key: routeKey,
            data: {
              farmName: farm.name,
              processName: animalSystem.name,
              animalType: animalSystem.animalType,
            },
          });
        },
        () => {
          if (active) fallBackToProcessPage();
        }
      );

      return () => {
        active = false;
      };
    }, [
      needsResolving,
      processData,
      farmID,
      processID,
      routeKey,
      fallBackToProcessPage,
    ]);

    if (!needsResolving) return <WrappedPage {...props} />;

    if (!processData) return <DsmLoadingIndicator />;

    return (
      <WrappedPage
        {...props}
        location={{ ...location, state: { ...routeState, ...processData } }}
      />
    );
  };

  PageWithResolvedRouteState.displayName = `withResolvedRouteState(${
    WrappedPage.displayName || WrappedPage.name || "Component"
  })`;

  return PageWithResolvedRouteState;
};

export default withResolvedRouteState;
